import { useState, type FormEvent } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthLayout, CodeInput, Button, HiyaLogo, useCountdown } from "@hiya/ui";
import { confirmSignInWithCode, signOut } from "../../auth/auth-service";

export function OtpPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const email = (location.state as { email?: string })?.email ?? "";
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { seconds, isActive, reset } = useCountdown(30);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await confirmSignInWithCode(code, email);
      if (result.isSignedIn) {
        navigate("/dashboard");
      } else {
        setError("Verification incomplete. Please try again.");
      }
    } catch {
      setError("The code doesn't match -- please try again.");
      setCode("");
    } finally {
      setLoading(false);
    }
  }

  // HH-250: clear the Cognito session BEFORE routing to /login. /login is
  // wrapped in <PublicRoute> which redirects to /dashboard on mount if a user
  // is still authenticated, so without signOut() a stale access token would
  // bounce the user straight to the dashboard without OTP re-entry. Also
  // wired to AuthLayout's back chevron so the same bypass is not reachable
  // via the top-left arrow.
  async function handleResend() {
    reset();
    try {
      await signOut();
    } catch {
      // swallow — we still want the user back on /login
    } finally {
      navigate("/login", { replace: true });
    }
  }

  return (
    <AuthLayout
      title="Check your Email"
      subtitle="We've sent a 6-digit verification code to your email. Enter the code below to continue."
      icon={<HiyaLogo />}
      onBack={handleResend}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-8 w-full">
        <CodeInput value={code} onChange={setCode} hasError={!!error} />
        {error && (
          <p className="text-xs font-medium text-input-hint-error text-center">{error}</p>
        )}
        <div className="flex flex-col gap-4 items-center w-full">
          <Button type="submit" disabled={code.length < 6 || loading}>
            Log in
          </Button>
          <p className="text-base font-semibold text-text-primary text-center">
            {isActive ? (
              <>{"New code in "}<span className="text-brand-brown">{seconds} seconds</span></>
            ) : (
              <button
                type="button"
                onClick={handleResend}
                className="font-semibold text-brand-brown underline"
              >
                Sign in again to get a new code
              </button>
            )}
          </p>
        </div>
      </form>
    </AuthLayout>
  );
}
